import { createFileRoute, Link } from '@tanstack/react-router'
import { ArticleMarkdown } from '@/components/ArticleMarkdown'
import { formatArticleDate, getArticle } from '@/lib/articles'

export const Route = createFileRoute('/articles/$slug')({
  loader: ({ params }) => ({ article: getArticle(params.slug) }),
  head: ({ loaderData }) => {
    const article = loaderData?.article
    if (!article) return { meta: [{ title: 'Article not found — Halo Compliance' }] }

    const url = `https://www.halocompliance.com/articles/${article.slug}`
    const title = `${article.title} — Halo Compliance`
    return {
      meta: [
        { title },
        { name: 'description', content: article.summary },
        // Drafts are dev-only, but tag them anyway so a draft that reaches a
        // crawler by any route is never indexed.
        ...(article.draft ? [{ name: 'robots', content: 'noindex' }] : []),
        { property: 'og:type', content: 'article' },
        { property: 'og:title', content: title },
        { property: 'og:description', content: article.summary },
        { property: 'og:url', content: url },
        ...(article.image
          ? [{ property: 'og:image', content: `https://www.halocompliance.com${article.image}` }]
          : []),
        { property: 'article:published_time', content: article.date },
        { property: 'article:author', content: article.author },
      ],
      links: [{ rel: 'canonical', href: url }],
    }
  },
  component: ArticlePage,
})

function ArticlePage() {
  const { article } = Route.useLoaderData()

  if (!article) {
    return (
      <div className="marketing-inner py-5">
        <div className="container py-4 text-center">
          <h1 className="h3 mb-3">Article not found</h1>
          <p className="text-body-secondary mb-4">
            This article may have been moved or is not published yet.
          </p>
          <Link to="/articles" className="btn btn-primary btn-sm px-3">
            Back to Articles
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="marketing-inner py-5">
      <div className="container py-4">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <article>
              <header className="mb-4">
                {article.draft && (
                  <span className="badge text-bg-warning mb-3">Draft — not published</span>
                )}
                <h1 className="display-6 font-serif mb-3">{article.title}</h1>
                <p className="small text-body-secondary mb-0">
                  <time dateTime={article.date}>{formatArticleDate(article.date)}</time>
                  <span className="mx-2">·</span>
                  {article.author}
                </p>
              </header>

              {/*
                `image` is deliberately NOT rendered here. It is the social /
                index card, and it carries the title as artwork — showing it
                under the real <h1> just prints the headline twice. It is used
                for og:image above and for the card on /articles.
              */}
              <ArticleMarkdown markdown={article.body} />
            </article>

            <div className="mt-5 pt-4 border-top">
              <Link to="/articles" className="text-decoration-none">
                ← All articles
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
